import { useState, useEffect, useRef } from 'react';
import H3 from '@sdk/components/ui/typography/H3';
import gettext from '@sdk/lang/gettext';
import Text16 from '@sdk/components/ui/typography/Text16';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import styled from 'styled-components';
import TextField from '@sdk/components/forms/TextField';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import useSlingApi from '@sdk/api/useSlingApi';
import useAnalytics from '@sdk/analytics/useAnalytics';
import { AnalyticsEvents } from '@sdk/analytics/types';
import Clickable from '@sdk/components/primitives/Clickable';
import Text14 from '@sdk/components/ui/typography/Text14';
import Margin from '@sdk/components/ui/spacing/Margin';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';
import FieldError from '@sdk/components/forms/components/FieldError';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import useAuth from '@sdk/authentication/useAuth';

import getDefaultPositions, {
  FormPosition,
  getPositionForIndex,
} from '@web/onboarding/helpers/getDefaultPositions';

const MAX_INPUT_LENGTH = 50;

const Container = styled(Column)`
  max-width: 453px;
  flex-grow: 1;
  flex-shrink: 1;
`;

const FieldContainer = styled(Column)`
  margin-top: 15px;
`;

const ButtonContainer = styled(Row)`
  padding-bottom: 40px;
  justify-content: flex-end;
  align-items: center;
`;

const Button = styled(ProgressButton)`
  flex-grow: 0.5;
  flex-basis: 0;
`;

const SkipButton = styled(Clickable)`
  opacity: 0.65;
`;

type Props = {
  onSuccess: () => void;
  onSkip: () => void;
  addPersonaCollectionGroup: (group: { group: any }) => void;
};

export const PositionsStep = ({ onSuccess, onSkip, addPersonaCollectionGroup }: Props) => {
  const { currentUser } = useAuth();
  const api = useSlingApi();
  const [positions, setPositions] = useState<FormPosition[]>();
  const { track } = useAnalytics({
    userId: currentUser.id,
    email: currentUser.email,
    orgId: currentUser.org.id,
    toastRestaurantGuids: currentUser.org.toastRestaurantGuids,
  });
  const focusFieldRef = useRef(null);
  const wasFocusedRef = useRef(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  useEffect(() => {
    if (focusFieldRef.current && !wasFocusedRef.current) {
      focusFieldRef.current.focus();
      wasFocusedRef.current = true;
    }
  });

  useEffect(() => {
    if (currentUser && !positions) {
      const updatedPositions = getDefaultPositions(currentUser.org.businessCategory);

      setPositions(updatedPositions);
    }
  }, [positions, currentUser]);

  const addPosition = (): void => {
    const newPosition = getPositionForIndex(positions.length, currentUser.org.businessCategory);
    setPositions([...positions, newPosition]);
  };

  const setPositionName = (index: number, name: string): void => {
    const updatedPositions = [...positions];
    if (updatedPositions[index]) {
      updatedPositions[index].name = name;
      setPositions(updatedPositions);
    }

    if (index === positions.length - 1 && name) {
      addPosition();
    }
  };

  const isPositionNameInvalid = (index: number): boolean => {
    const name = positions?.[index]?.name;

    if (name?.length > MAX_INPUT_LENGTH) {
      return true;
    }

    if (name?.trim()) {
      return positions?.filter((p) => p.name === name).length > 1;
    }

    return false;
  };

  const validate = (): boolean => {
    return !positions?.some((_, i) => isPositionNameInvalid(i));
  };

  const submit = async (): Promise<void> => {
    if (!validate()) {
      throw 'Form invalid';
    }

    const positionsPromises = positions
      .filter((p) => p.name?.trim())
      .map((p) => {
        const savePosition = { ...p };
        delete savePosition.placeholder;
        return api.positions.createPosition(savePosition);
      });

    try {
      const savedPositionsResponse = await Promise.all(positionsPromises);
      savedPositionsResponse.map((p) => addPersonaCollectionGroup({ group: p.data }));

      if (savedPositionsResponse.length) {
        track(AnalyticsEvents.orgSetupAddedPositions, savedPositionsResponse.length);
      }

      onSuccess();
    } catch (e) {
      if (e?.data?.errors) {
        setServerErrors(e.data.errors);
        return;
      }

      if (e?.data?.message) {
        setServerErrors([e.data.message]);
        return;
      }

      setServerErrors([GENERIC_SERVER_ERROR]);
    }
  };

  if (!positions) {
    return null;
  }

  return (
    <Container>
      <H3 top={20} bottom={10}>
        {gettext('Set up your schedule')}
      </H3>
      <Text16 themeColor={ThemeColorType.gray}>
        {gettext("Almost there! Add positions so it's easier to build a schedule.")}
      </Text16>
      <FieldContainer />
      {positions.map((p, i) => (
        <FieldContainer key={i}>
          <TextField
            ref={i === 0 ? focusFieldRef : null}
            label={gettext('Position name')}
            value={p.name}
            placeholder={i === 0 ? p.placeholder : null}
            onChange={(e): void => setPositionName(i, e.currentTarget.value)}
            invalid={isPositionNameInvalid(i)}
            maxLength={MAX_INPUT_LENGTH}
            height={INPUT_HEIGHT}
          />
        </FieldContainer>
      ))}

      {!!serverErrors.length && (
        <Column top={15}>
          {serverErrors.map((error, i) => (
            <FieldError invalid={true} error={error} key={i} />
          ))}
        </Column>
      )}

      <ButtonContainer top={40}>
        <Margin right={20}>
          <SkipButton onClick={onSkip}>
            <Text14 themeColor={ThemeColorType.gray} underline>
              {gettext("I'll do this later")}
            </Text14>
          </SkipButton>
        </Margin>

        <Button buttonType={ButtonType.primary} onClick={submit} height={INPUT_HEIGHT}>
          {gettext('Next')}
        </Button>
      </ButtonContainer>
    </Container>
  );
};
